import { PublicShell } from "@/components/public/public-shell";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { getBousalaReport, generateBousalaReport } from "@/lib/bousala/report.functions";
import { BousalaReportView } from "@/components/bousala/report/report-view";
import { ReportRecoveryLinkCard } from "@/components/bousala/report/recovery-link-card";
import { ContactCaptureCard } from "@/components/bousala/report/contact-capture-card";
import { SaveResultSheet } from "@/components/bousala/report/save-result-sheet";
import { CONFIDENCE_BADGE_LABEL } from "@/data/bousala-v1.report-config";
import { SmartUpsellCard } from "@/components/bousala/report/smart-upsell-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { asLang, dirFor, type Lang } from "@/lib/bousala/i18n";

export const Route = createFileRoute("/_authenticated/portal/bousala/$attemptId")({
  ssr: false,
  validateSearch: z.object({ checkout: z.enum(["success"]).optional() }),
  head: () => ({
    meta: [
      { title: "تقرير بوصلة د. سارة" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: () => (
    <PublicShell ground="bg-cream-200">
      <ReportPage />
    </PublicShell>
  ),
});

function ReportPage() {
  const { attemptId } = Route.useParams();
  const search = Route.useSearch();
  const router = useRouter();
  const fetchReport = useServerFn(getBousalaReport);
  const fnGenerate = useServerFn(generateBousalaReport);

  const q = useQuery({
    queryKey: ["bousala", "report", attemptId],
    queryFn: () => fetchReport({ data: { attemptId } }),
  });

  const currentLang: Lang = asLang((q.data as any)?.language ?? "ar");
  const dir = dirFor(currentLang);
  const t = currentLang === "en"
    ? {
        preparing: "Preparing your full report…",
        opened: "Your full report is unlocked",
        preparingTitle: "Your report is being prepared",
        inProgress: "You haven't finished the test yet.",
        scoring: "You finished the test — we're scoring your answers.",
        scored: "Your test is scored — click below to generate the report.",
        generateNow: "Generate report now",
        generating: "Generating…",
        backPortal: "Back to portal",
        reportGenerated: "Report generated",
        genericError: "Something went wrong",
      }
    : {
        preparing: "شكراً! نحضّر تقريرك الكامل الآن...",
        opened: "تم فتح تقريرك الكامل",
        preparingTitle: "تقريرك قيد التحضير",
        inProgress: "لم تكمل الاختبار بعد.",
        scoring: "أنهِت الاختبار — نعمل على تصحيح إجاباتك.",
        scored: "الاختبار مصحّح، والزر يولّد التقرير.",
        generateNow: "توليد التقرير الآن",
        generating: "جارٍ التوليد...",
        backPortal: "العودة للبورتال",
        reportGenerated: "تم توليد التقرير",
        genericError: "حدث خطأ",
      };

  // Poll after returning from Paddle checkout until webhook flips the tier.
  const pollStartedAt = useRef<number | null>(null);
  useEffect(() => {
    if (search.checkout !== "success") return;
    if (pollStartedAt.current === null) {
      pollStartedAt.current = Date.now();
      toast.success(t.preparing);
    }
    if (q.data && q.data.tier !== "free") {
      toast.success(t.opened);
      // Clear the ?checkout=success param without a refetch loop.
      router.navigate({
        to: "/portal/bousala/$attemptId",
        params: { attemptId },
        search: {},
        replace: true,
      });
      return;
    }
    // Give the webhook up to ~60s, refetching every 3s.
    const elapsed = Date.now() - (pollStartedAt.current ?? Date.now());
    if (elapsed > 60_000) return;
    const timer = setTimeout(() => q.refetch(), 3000);
    return () => clearTimeout(timer);
  }, [search.checkout, q.data, q, router, attemptId, t.preparing, t.opened]);

  const generate = useMutation({
    mutationFn: () => fnGenerate({ data: { attemptId } }),
    onSuccess: () => {
      toast.success(t.reportGenerated);
      router.invalidate();
      q.refetch();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t.genericError),
  });

  // Auto-generate the report the moment the user lands here after finishing
  // the test, so nobody has to click a "generate now" button.
  const autoGenTriedRef = useRef(false);
  useEffect(() => {
    const d = q.data;
    if (!d) return;
    if (d.model) return;
    if (autoGenTriedRef.current) return;
    if (generate.isPending) return;
    if (
      d.status === "completed" ||
      d.status === "scored" ||
      d.status === "report_ready"
    ) {
      autoGenTriedRef.current = true;
      generate.mutate();
    }
  }, [q.data, generate]);

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center py-24" dir={dir}>
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <span className="sr-only" role="status" aria-live="polite">
          {currentLang === "en" ? "Loading your report…" : "جارٍ تحميل تقريرك…"}
        </span>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4" dir={dir}>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">{(q.error as Error).message}</p>
            <Button asChild variant="secondary" className="mt-4"><Link to="/portal">{t.backPortal}</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const data = q.data;
  if (!data) return null;

  if (!data.model) {
    const isFinished =
      data.status === "completed" ||
      data.status === "scored" ||
      data.status === "report_ready";
    const autoBusy = isFinished && (generate.isPending || !generate.isError);
    return (
      <div className="max-w-2xl mx-auto py-12 px-4" dir={dir}>
        <Card>
          <CardHeader>
            <CardTitle>{t.preparingTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-brown-500">
              {data.status === "in_progress"
                ? t.inProgress
                : data.status === "completed"
                ? t.scoring
                : t.scored}
            </p>
            {autoBusy ? (
              <div className="flex items-center gap-2 text-sm text-brown-500" role="status" aria-live="polite">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span>{t.generating}</span>
              </div>
            ) : (
              <Button
                onClick={() => {
                  autoGenTriedRef.current = true;
                  generate.mutate();
                }}
                disabled={generate.isPending || data.status === "in_progress"}
              >
                {generate.isPending ? t.generating : t.generateNow}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const level = (data.model as { confidence?: { level?: "high" | "medium" | "low" } }).confidence?.level;
  return (
    <div className="py-8 px-4" dir={dir}>
      {currentLang !== "en" && (
        <header className="mx-auto mb-8 max-w-3xl">
          <p className="font-display text-[13px] font-bold text-amber-800">تقريرك جاهز</p>
          <h1 className="mt-2 font-display text-[36px] font-extrabold leading-tight text-text-primary">
            نتيجتك <span className="mark">طلعت</span>.
          </h1>
          <p className="mt-2 text-[17px] leading-[1.85] text-text-secondary">8 محاور، وتحت كل محور شو بيعني لقرار التخصص.</p>
          {level && (
            <span className="mt-3 inline-block rounded-ds-pill bg-green-100 px-3 py-1 font-display text-[13px] font-bold text-green-800">
              مؤشر الموثوقية: {CONFIDENCE_BADGE_LABEL[level]}
            </span>
          )}
        </header>
      )}
      <SaveResultSheet attemptId={attemptId} shareToken={data.shareToken ?? null} />
      {data.shareToken && (
        <div className="mx-auto mb-6 max-w-3xl">
          <ReportRecoveryLinkCard
            shareToken={data.shareToken}
            lang={currentLang}
            redirectTo={`/portal/bousala/${attemptId}`}
          />
        </div>
      )}
      <div className="mx-auto mb-8 max-w-3xl">
        <ContactCaptureCard lang={currentLang} />
      </div>
      <div className="mx-auto mb-8 max-w-3xl">
        <SmartUpsellCard lang={currentLang} />
      </div>
      <BousalaReportView model={data.model} tier={data.tier} attemptId={attemptId} stats={data.stats} lang={currentLang} titleAs={currentLang !== "en" ? "h2" : "h1"} shareToken={data.shareToken ?? null} />
    </div>
  );
}